import { Navigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useActiveClinic } from '@/lib/activeClinic';
import { useClinic } from '@/hooks/useClinicSettings';
import {
  isClinicModuleEnabled,
  readActivatedModules,
} from '@/lib/clinicModules';
import type { PermissionAction, PermissionResource } from '@/lib/permissions';

interface PermissionRouteProps {
  /** Single resource (legacy) or any-of list. */
  resource?: PermissionResource;
  anyOf?: PermissionResource[];
  action?: PermissionAction;
  /** Optional clinic module that must be enabled (see `activated_modules`). */
  moduleKey?: string;
  children: React.ReactNode;
  fallbackTo?: string;
}

/** Blocks route content when the active clinic membership lacks permission. */
export function PermissionRoute({
  resource,
  anyOf,
  action = 'view',
  moduleKey,
  children,
  fallbackTo = '/dashboard',
}: PermissionRouteProps) {
  const { hasPermission, status } = useAuth();
  const { activeClinicId } = useActiveClinic();
  const { data: clinic, isLoading: clinicLoading } = useClinic(
    moduleKey ? activeClinicId : null,
  );

  if (status === 'initializing') return null;
  if (moduleKey && clinicLoading) return null;

  if (moduleKey) {
    const modules = readActivatedModules(clinic?.activated_modules);
    if (!isClinicModuleEnabled(modules, moduleKey)) {
      return <Navigate to={fallbackTo} replace />;
    }
  }

  const resources = anyOf ?? (resource ? [resource] : []);
  const allowed = resources.some((r) => hasPermission(activeClinicId, r, action));

  if (!allowed) {
    return <Navigate to={fallbackTo} replace />;
  }

  return <>{children}</>;
}
